import { useRef, useMemo, useState, Activity, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';

import {
  IconEye,
  IconPlus,
  IconSearch,
  IconAdjustments,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ClassSummaryCard from '@/components/ClassSummaryCard';
import CustomImage from '@/components/CustomImage';
import EmptyState from '@/components/EmptyState';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import TeacherClassDetailsSkeleton from '@/components/SkeletonLoader/TeacherClassDetailsSkeleton';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import { LessonStatusConfig } from '@/constants/constant';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getTeacherClassDetails,
  getTeacherClassStudents,
  getTeacherClassLessonChart,
} from '@/service/classService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import {
  ILessonChartItem,
  ITeacherClassStudent,
  ITeacherClassDetails,
} from '@/types/class';
import { getAssetUrl, formatLastActive } from '@/utils';

import ClassStudentFilterModal, {
  IClassDetailsFilters,
} from './components/ClassStudentFilterModal';
import LessonBreakdownCard from './components/LessonBreakdownCard';
import LessonCompletionChart from './components/LessonCompletionChart';
import { StudentDetailDrawer } from './components/StudentDetailDrawer';
import TeacherLessonDetailsDrawer from './components/TeacherLessonDetailsDrawer';

const TeacherClassDetails = () => {
  const { formatText } = useFormatText();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const { isTableLoading, setTableLoading, isScreenLoading, setScreenLoading } =
    useSkeletonStore();

  const [classDetails, setClassDetails] = useState<ITeacherClassDetails | null>(
    null
  );
  const [students, setStudents] = useState<ITeacherClassStudent[]>([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'' | 'ASC' | 'DESC'>('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<IClassDetailsFilters | null>(null);
  const [isStudentDrawerOpen, setIsStudentDrawerOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [isLessonDrawerOpen, setIsLessonDrawerOpen] = useState(false);
  const [lessonChartData, setLessonChartData] = useState<ILessonChartItem[]>(
    []
  );

  const isInitialLoad = useRef(true);
  const pageTitle = formatText('teacher-classes.title');
  const classTitle = classDetails?.name || '...';

  const { register, watch } = useForm({
    defaultValues: {
      search: '',
    },
  });

  const search = watch('search');
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    setTitle(pageTitle);
    setIsBreadcrumb([
      { label: pageTitle, to: '/teacher/classes' },
      { label: classTitle },
    ]);

    return () => {
      setTitle('');
      setIsBreadcrumb([]);
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classTitle, pageTitle]);

  const fetchClassDetails = async () => {
    if (!id) return;
    setScreenLoading(true);
    try {
      const response = await getTeacherClassDetails(id);
      if (response?.status && response?.data) {
        // Teacher class details endpoint might include lesson breakdown
        setClassDetails(response.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!isTableLoading) setScreenLoading(false);
    }
  };

  const fetchStudents = async () => {
    if (!id) return;

    const isFirstLoad = isInitialLoad.current;
    if (isFirstLoad) setScreenLoading(true);
    else setTableLoading(true);

    try {
      const query: Record<string, unknown> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(sortField && { [`order[${sortField}]`]: sortDirection }),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.studentName?.length && {
          student_id: filters.studentName,
        }),
        ...(filters?.lesson_id?.length && {
          lesson_id: filters.lesson_id,
        }),
      };

      const response = await getTeacherClassStudents(id, query);

      if (response?.status) {
        setStudents(response.data ?? []);
        setTotalStudents(response.total ?? 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setScreenLoading(false);
      setTableLoading(false);
      isInitialLoad.current = false;
    }
  };

  const fetchLessonChart = async () => {
    if (!id) return;
    try {
      const response = await getTeacherClassLessonChart(id);
      if (response?.status && response?.data) {
        setLessonChartData(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchClassDetails();
    fetchLessonChart();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchStudents();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentPage, debouncedSearch, sortField, sortDirection, filters]);

  const handleSort = (key: string) => {
    if (sortField === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortField('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortField(key);
      setSortDirection('ASC');
    }
  };
  const handleApplyFilters = (newFilters: IClassDetailsFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleRowViewClick = (RowId: string) => {
    if (id) {
      setSelectedStudent(RowId);
      setIsStudentDrawerOpen(true);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: 'class-details.students-table.columns.student-name',
        sorter: true,
        handleSort: () => handleSort('name'),
        render: (record: ITeacherClassStudent) =>
          record?.name ? (
            <div className="flex items-center gap-2">
              <AvatarGroupList
                list={[
                  {
                    id: record?.id,
                    name: record?.name,
                    image: getAssetUrl(record?.profile_pic),
                  },
                ]}
                size={40}
                avatarClassName="w-10 h-10 text-xs"
              />
              <span className="text-sm font-medium text-neutral-primary">
                {record?.name}
              </span>
            </div>
          ) : (
            '-'
          ),
      },
      {
        key: 'current_lesson',
        title: 'class-details.students-table.columns.current-lesson-name',
        sorter: true,
        handleSort: () => handleSort('current_lesson_name'),
        render: (record: ITeacherClassStudent) => (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E0F2FE] flex items-center justify-center overflow-hidden shrink-0 border border-[#B9E6FE]">
              <CustomImage
                src={record?.current_lesson_img || '/assets/images/lesson.jpg'}
                alt={record?.current_lesson_name || ''}
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-neutral-primary text-sm line-clamp-1">
              {record?.current_lesson_name || '-'}
            </span>
          </div>
        ),
      },
      {
        key: 'progress',
        title: 'class-details.students-table.columns.progress',
        sorter: true,
        handleSort: () => handleSort('progress'),
        render: (record: ITeacherClassStudent) => (
          <div className="flex items-center gap-3">
            <ProgressBar
              value={record?.progress || 0}
              showPercentage={false}
              height="h-2"
              barColor="bg-[#7F56D9]"
              backgroundColor="bg-[#F2F4F7]"
              className="w-32"
            />
            <span className="text-xs font-medium text-[#475467]">
              {record?.progress || 0}%
            </span>
          </div>
        ),
      },
      {
        key: 'accuracy',
        title: 'teacher-classes.columns.accuracy',
        sorter: true,
        handleSort: () => handleSort('accuracy'),
        render: (record: ITeacherClassStudent) => (
          <span>{record?.accuracy ?? 0}%</span>
        ),
      },
      {
        key: 'status',
        title: 'teacher-classes.columns.status',
        sorter: true,
        handleSort: () => handleSort('status'),
        render: (record: ITeacherClassStudent) => {
          const config = LessonStatusConfig[record?.status];
          return (
            <TagCapsule
              value={formatText(config.label)}
              className={config.className}
            />
          );
        },
      },
      {
        key: 'last_active',
        title: 'teacher-classes.details.last-active',
        sorter: true,
        handleSort: () => handleSort('last_active'),
        render: (record: ITeacherClassStudent) => (
          <span className="text-neutral-secondary text-sm">
            {formatLastActive(
              record?.last_active ? Number(record.last_active) : 0,
              formatText
            )}
          </span>
        ),
      },
      {
        key: 'action',
        title: 'common.actions.action',
        className: 'text-right',
        render: (record: ITeacherClassStudent) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'common.actions.view',
                icon: <IconEye size={18} />,
                onClick: () => {
                  handleRowViewClick(record?.id);
                },
              },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortDirection, sortField, formatText]
  );

  const isFilterApplied =
    !!filters?.status ||
    (filters?.studentName && filters.studentName.length > 0) ||
    (filters?.lesson_id && filters.lesson_id.length > 0);

  const isEmptySearch: boolean =
    !isScreenLoading &&
    !isTableLoading &&
    students.length === 0 &&
    (!!debouncedSearch || !!isFilterApplied);

  const isEmptyTable: boolean =
    !isScreenLoading &&
    !isTableLoading &&
    students.length === 0 &&
    !debouncedSearch &&
    !isFilterApplied;

  if (isScreenLoading) {
    return <TeacherClassDetailsSkeleton columns={columns} />;
  }

  return (
    <div className="flex flex-col gap-8" data-testid="class-details-page">
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-4 ps-1">
          <Input
            name="search"
            register={register}
            placeholder={formatText(
              'teacher-classes.details.search-placeholder'
            )}
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="max-w-75"
            inputClassName="py-[13px]"
          />
          <div className="relative">
            <Button
              variant="outline"
              label={formatText('common.filter')}
              icon={<IconAdjustments size={20} />}
              className={`h-11 ${isFilterApplied ? 'border-brand-default! text-brand-default!' : ''}`}
              onClick={() => setIsFilterModalOpen(true)}
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>
      </div>

      {/* Class Overview */}
      <div className="flex flex-col gap-4" data-testid="class-overview-section">
        <h2 className="text-lg font-bold text-neutral-primary">
          {formatText('teacher-classes.details.class-overview')}
        </h2>

        <div className="grid lg:grid-cols-[2fr_1fr] gap-6 items-start">
          {/* Lesson Completion Trend Chart */}
          <div className="bg-card rounded-xl border border-neutral-light-200 p-5">
            <h3 className="text-base font-semibold text-neutral-primary mb-4">
              {formatText('teacher-classes.details.lesson-completion-trend')}
            </h3>
            <LessonCompletionChart lessons={lessonChartData} />
          </div>

          {/* Class Info Card */}
          <ClassSummaryCard
            name={classDetails?.name || ''}
            studentsCount={classDetails?.students || 0}
            lessonsCount={classDetails?.lessons || 0}
            assignedTo={classDetails?.assigned_class_teacher || undefined}
            avgProgress={classDetails?.average_progress || 0}
            className="cursor-pointer hover:shadow-sm max-w-full! h-full"
          />
        </div>
      </div>

      {/* Lesson Breakdown */}
      <div
        className="flex flex-col gap-4"
        data-testid="lesson-breakdown-section"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-neutral-primary">
            {formatText('teacher-classes.details.lesson-list')}
          </h3>
          <div className="flex items-center gap-3">
            {classDetails && classDetails?.lessons > 4 && (
              <Button
                variant="outline"
                label={formatText('common.actions.view-all')}
                onClick={() =>
                  navigate(`/teacher/classes/${id}/lesson-breakdown`, {
                    state: {
                      className: classDetails?.name,
                    },
                  })
                }
              />
            )}
            <Button
              variant="primary"
              label={formatText('lessons.create-lesson')}
              icon={<IconPlus size={18} />}
              onClick={() =>
                navigate(`/teacher/classes/${id}/create-lesson`, {
                  state: { className: classTitle },
                })
              }
            />
          </div>
        </div>

        {classDetails && classDetails.lesson_breakdown?.length > 0 ? (
          <div className="grid xl:grid-cols-4 grid-cols-2 gap-6 overflow-x-auto pb-2 scrollbar-hide">
            {classDetails.lesson_breakdown.slice(0, 4).map((lesson) => (
              <LessonBreakdownCard
                key={lesson.id}
                name={lesson.name}
                assignedDate={lesson.assigned_date ?? ''}
                dueDate={lesson.due_date ?? ''}
                progress={lesson.average_progress || 0}
                status={lesson.status || ''}
                onClick={() => {
                  setSelectedLessonId(lesson.id);
                  setIsLessonDrawerOpen(true);
                }}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            imageSrc="/assets/images/lesson-breakdown-empty.png"
            title={formatText('common.no-data-found')}
            className="h-75! w-full"
          />
        )}
      </div>

      <div
        className="flex flex-col gap-6"
        data-testid="student-progress-section"
      >
        <h3 className="text-lg font-bold text-neutral-primary ps-1">
          {formatText('teacher-classes.details.student-progress-overview')}
        </h3>

        <CustomTable
          isLoading={isTableLoading}
          columns={columns}
          dataSource={students}
          total={totalStudents}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          fieldName={sortField}
          sortDirection={sortDirection}
          isEmpty={isEmptyTable}
          isEmptySearch={isEmptySearch}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          onRowClick={handleRowViewClick}
          emptyClassName="h-[300px] w-full"
        />
      </div>

      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <ClassStudentFilterModal
          isOpen={isFilterModalOpen}
          setIsOpen={setIsFilterModalOpen}
          initialFilters={filters}
          onApplyFilters={handleApplyFilters}
        />
      </Activity>
      <Activity
        mode={isStudentDrawerOpen && selectedStudent ? 'visible' : 'hidden'}
      >
        <StudentDetailDrawer
          isOpen={isStudentDrawerOpen}
          onClose={() => {
            setIsStudentDrawerOpen(false);
            setSelectedStudent(null);
          }}
          studentId={selectedStudent}
          classId={id || ''}
        />
      </Activity>

      <Activity
        mode={isLessonDrawerOpen && selectedLessonId ? 'visible' : 'hidden'}
      >
        <TeacherLessonDetailsDrawer
          isOpen={isLessonDrawerOpen}
          onClose={() => {
            setIsLessonDrawerOpen(false);
            setSelectedLessonId(null);
          }}
          lessonId={selectedLessonId}
          onSuccess={fetchClassDetails}
          className={classTitle}
        />
      </Activity>
    </div>
  );
};

export default TeacherClassDetails;
